/**
 * The campaign-group switcher — ONE dropdown on the right of the views. Its
 * trigger names the open group (crown in its master config's color, label,
 * containers) or "All campaign groups"; the list searches every group of the
 * country by name, campaign or ID: All · Pinned · In this plan · Other
 * groups in ‹country›. Nothing scrolls sideways.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Crown, Layers, Search } from 'lucide-react';

import { type GroupEntry, middleTruncate } from './campaign-scope';
import { SwitcherRow } from './SwitcherRow';

interface Props {
  entries: GroupEntry[];
  current: GroupEntry | null;
  countryName: string;
  totalContainers: number;
  canPin: boolean;
  onSelect: (key: string | null) => void;
  onPin: (entry: GroupEntry, on: boolean) => void;
}

const matches = (e: GroupEntry, q: string) =>
  e.fullLabel.toLowerCase().includes(q) || e.members.some((m) => String(m.wiserId) === q);

export const GroupSwitcher: FC<Props> = ({ entries, current, countryName, totalContainers, canPin, onSelect, onPin }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? entries.filter((e) => matches(e, q)) : entries;
  }, [entries, search]);
  const pick = (key: string | null) => {
    onSelect(key);
    setSearch('');
    setOpen(false);
  };
  const rowProps = { current: current?.key ?? null, canPin, onSelect: pick, onPin };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Campaign group: ${current?.label ?? 'All campaign groups'} — switch`}
          title={current?.fullLabel}
          className="flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors hover:bg-accent sm:w-auto sm:max-w-[380px]"
        >
          {current?.master ? <Crown className="h-3.5 w-3.5 shrink-0" style={{ color: current.master.color }} /> : <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          <span className="truncate font-medium">{current ? middleTruncate(current.label, 34) : 'All campaign groups'}</span>
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{current?.containerCount ?? totalContainers}</span>
          <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} collisionPadding={12} onCloseAutoFocus={(e) => e.preventDefault()} className="z-[250] w-[min(460px,92vw)] rounded-lg border border-border bg-popover shadow-lg">
          <div className="border-b border-border p-2">
            <label className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input type="search" autoFocus autoComplete="off" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search a campaign, group or ID…" aria-label="Search campaign groups" className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
            </label>
          </div>
          <div className="max-h-[min(420px,60vh)] overflow-y-auto p-1">
            {search.trim() ? null : <SwitcherRow all total={totalContainers} {...rowProps} />}
            <Section title="Pinned" rows={shown.filter((e) => e.pinned)} rowProps={rowProps} />
            <Section title="In this plan" rows={shown.filter((e) => e.inPlan && !e.pinned)} rowProps={rowProps} />
            <Section title={`Other groups in ${countryName}`} rows={shown.filter((e) => !e.inPlan && !e.pinned)} rowProps={rowProps} />
            {shown.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No group matches</p> : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

type RowProps = Omit<Parameters<typeof SwitcherRow>[0], 'entry' | 'all' | 'total'>;

const Section: FC<{ title: string; rows: GroupEntry[]; rowProps: RowProps }> = ({ title, rows, rowProps }) =>
  rows.length === 0 ? null : (
    <div className="pt-1">
      <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {rows.map((e) => <SwitcherRow key={e.key} entry={e} {...rowProps} />)}
    </div>
  );
